import httpClient from '~/lib/http-client';
import type { AccountPlan } from '~/lib/api/plans';

export interface BillingSubscription {
  tier: 'free' | 'starter' | 'growth' | 'pro' | 'scale';
  status: 'none' | 'active' | 'cancelled' | 'past_due' | 'on_hold';
  creditsAllocated: number;
  creditsRemaining: number;
  renewsAt: string | null;
  /** the plan behind the one balance (Plus/Pro sit here while `tier` stays 'free'); absent on older servers */
  plan?: AccountPlan;
}

export async function apiGetSubscription(): Promise<BillingSubscription> {
  const { data } = await httpClient.get<BillingSubscription>('/api/billing/subscription');
  return data;
}

// ─── Subscriptions & billing (DEV-39, Phase 2) ───

/**
 * Website plan tiers. None of them is sold any more — Dunea sells Free/Plus/Pro
 * on one balance (see ~/lib/api/plans) — but existing subscribers keep theirs
 * until they cancel, so the website still reads and manages them.
 */
export type SubscriptionTier = 'free' | 'starter' | 'growth' | 'pro' | 'scale';
/** Billing cadence. Annual bills once for 10 months (2 free) but credits refresh all 12. */
export type BillingInterval = 'month' | 'year';
export type SubscriptionStatus =
  | 'none'
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'unpaid'
  | 'cancelled'
  | 'incomplete'
  | 'on_hold';

export interface PaymentMethodSummary {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

/**
 * The single source of truth for subscription state across the app. Tier/status/
 * credits are DB-authoritative (webhook-synced); price, dates, cancellation
 * schedule and payment method come live from Stripe. Query key: `['billing-overview']`.
 */
export interface BillingOverview {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  creditsAllocated: number;
  creditsRemaining: number;
  billingEmail: string | null;
  /** Recurring charge for one billing period (monthly price, or yearly price if annual). Pair with `interval`. */
  priceMonthly: number | null;
  /** Billing cadence of the active subscription. 'month' unless it's an annual plan. */
  interval: BillingInterval;
  currency: string;
  subscriptionId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  startedAt: string | null;
  cancelAtPeriodEnd: boolean;
  cancelAt: string | null;
  autoRenew: boolean;
  paymentMethod: PaymentMethodSummary | null;
  /** The plan active RIGHT NOW — the UI's source of truth. A scheduled downgrade does not change this. */
  activePlan: SubscriptionTier | null;
  /** A lower plan scheduled to take effect at the next renewal, else null. */
  scheduledPlan: SubscriptionTier | null;
  /** When `scheduledPlan` takes effect (= current period end), else null. */
  effectiveDate: string | null;
  /**
   * The plan behind the account's ONE balance, whichever product holds it: a
   * grandfathered website plan (`product: 'web'`), Plus/Pro bought here or in
   * Dunea Desktop (`'code'`) or Dunea Work (`'work'`), or Free (`null`).
   * Optional so an older server that predates it doesn't break the client.
   */
  accountPlan?: AccountPlan;
}

export interface InvoiceSummary {
  id: string;
  number: string | null;
  amount: number;
  currency: string;
  status: string | null;
  created: string;
  description: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

/** Client-side Stripe config (publishable key) for Stripe Elements. Query key: `['billing-config']`. */
export interface BillingConfig {
  /** Active payment provider. Stripe-only UI (Elements card entry, in-app charges) is hidden unless 'stripe'. */
  provider?: 'stripe' | 'dodo' | 'razorpay';
  publishableKey: string | null;
  configured: boolean;
}

/** Result of scheduling/clearing an end-of-period cancellation. */
export interface CancellationState {
  cancelAtPeriodEnd: boolean;
  cancelAt: string | null;
  currentPeriodEnd: string | null;
}

export async function apiGetBillingConfig(): Promise<BillingConfig> {
  const { data } = await httpClient.get<BillingConfig>('/api/billing/config');
  return data;
}

/** One plan's price in the caller's currency, ex-tax, in major units. */
export interface LocalizedPrice {
  amount: number;
  value: number;
}

/**
 * Plan prices in the caller's own currency, resolved server-side from Dodo so the
 * figure shown here is the figure the hosted checkout will charge — including its
 * rounding, which we cannot reproduce locally.
 *
 * Keys are `<tier>_<month|year>`. `{ currency: 'USD', prices: {} }` means "use the
 * built-in USD catalog", and is what comes back for USD countries, unmapped
 * countries and provider failures alike, so there is one fallback path.
 *
 * Amounts are EX-TAX, like the USD prices have always been: India's 18% GST and US
 * sales tax alike are added at checkout.
 */
export interface LocalizedPriceTable {
  currency: string;
  prices: Record<string, LocalizedPrice>;
}

export async function apiGetLocalizedPrices(): Promise<LocalizedPriceTable> {
  const { data } = await httpClient.get<LocalizedPriceTable>('/api/billing/localized-prices');
  return data;
}

export async function apiGetBillingOverview(): Promise<BillingOverview> {
  const { data } = await httpClient.get<BillingOverview>('/api/billing/overview');
  return data;
}

export async function apiGetInvoices(): Promise<InvoiceSummary[]> {
  const { data } = await httpClient.get<{ invoices: InvoiceSummary[] }>('/api/billing/invoices');
  return data.invoices;
}

/**
 * Download an invoice PDF served by our own API (Dodo, whose PDF needs server auth).
 * Fetches with the bearer token as a blob and triggers a browser download — a plain
 * `<a href>` can't authenticate. Providers with public PDF URLs (Stripe) open directly.
 */
export async function apiDownloadInvoicePdf(url: string, filename: string): Promise<void> {
  const { data } = await httpClient.get<Blob>(url, { responseType: 'blob' });
  const objectUrl = window.URL.createObjectURL(data);

  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    window.URL.revokeObjectURL(objectUrl);
  }
}

// Website plan checkouts (/api/billing/checkout/subscription, the Starter offer)
// are refused by the server: Plus and Pro are bought through ~/lib/api/plans.

// ─── One-time credit packs (DEV-124) ───

export type CreditPackId = 'small' | 'medium' | 'large';

/** A purchasable credit pack with its live one-time price (major units, e.g. dollars). */
export interface CreditPackOption {
  id: CreditPackId;
  label: string;
  credits: number;
  price: number | null;
  currency: string;
}

/** The credit packs available to buy, with live prices. Query key: `['credit-packs']`. */
export async function apiGetCreditPacks(): Promise<CreditPackOption[]> {
  const { data } = await httpClient.get<{ packs: CreditPackOption[] }>('/api/billing/credit-packs');
  return data.packs;
}

/** Start a one-time Stripe Checkout Session to buy a credit pack; returns the hosted URL. */
export async function apiCreateCreditPackCheckout(packId: CreditPackId): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>('/api/billing/checkout/credit-pack', { packId });
  return data;
}

// ─── Amount-based credit top-ups (Phase 3) ───

export interface CreditTopUpQuickPack {
  amountUsd: number;
  credits: number;
}

/** Conversion rate, bounds, and quick-pick amounts for credit top-ups. Query key: `['credit-topup-config']`. */
export interface CreditTopUpConfig {
  creditsPerDollar: number;
  min: number;
  max: number;
  packs: CreditTopUpQuickPack[];
}

export async function apiGetCreditTopUpConfig(): Promise<CreditTopUpConfig> {
  const { data } = await httpClient.get<CreditTopUpConfig>('/api/credits/quick-packs');
  return data;
}

/** Start a one-time Checkout Session to top up by `amount` USD; returns the hosted URL. */
export async function apiCreateCreditTopUpCheckout(amount: number): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>('/api/credits/checkout', { amount });
  return data;
}

/** Outcome of an in-app top-up charge. `requires_action` means the browser must complete 3D Secure. */
export interface TopUpPaymentResult {
  status: 'succeeded' | 'requires_action' | 'failed';
  clientSecret: string | null;
}

/** Charge a top-up to the saved card in-app (no redirect); may need 3D Secure. */
export async function apiPayCreditTopUp(amount: number): Promise<TopUpPaymentResult> {
  const { data } = await httpClient.post<TopUpPaymentResult>('/api/credits/pay', { amount });
  return data;
}

/** A single credit purchase (pack or top-up) for the purchase-history list. */
export interface CreditPurchase {
  id: string;
  date: string;
  amountPaid: number | null;
  currency: string;
  creditsGranted: number;
  invoiceId: string | null;
  status: string;
}

export async function apiGetCreditPurchases(): Promise<CreditPurchase[]> {
  const { data } = await httpClient.get<{ purchases: CreditPurchase[] }>('/api/credits/purchases');
  return data.purchases;
}

/** Balance breakdown: current, subscription, purchased, used, last top-up. Query key: `['credit-balance']`. */
export interface CreditBalanceBreakdown {
  currentBalance: number;
  subscriptionCredits: number;
  purchasedCredits: number;
  creditsUsed: number;
  lastTopUpAt: string | null;
}

export async function apiGetCreditBalance(): Promise<CreditBalanceBreakdown> {
  const { data } = await httpClient.get<CreditBalanceBreakdown>('/api/credits/balance');
  return data;
}

/** Withdraw a scheduled website-plan downgrade ("Keep current plan"). No charge. */
export async function apiKeepCurrentPlan(): Promise<{ ok: boolean }> {
  const { data } = await httpClient.post<{ ok: boolean }>('/api/billing/subscription/keep');
  return data;
}

/** Schedule an end-of-period cancellation (keeps access until the period ends). */
export async function apiCancelSubscription(): Promise<CancellationState> {
  const { data } = await httpClient.post<CancellationState>('/api/billing/subscription/cancel');
  return data;
}

/** Clear a pending cancellation so the subscription renews again. */
export async function apiResumeSubscription(): Promise<CancellationState> {
  const { data } = await httpClient.post<CancellationState>('/api/billing/subscription/resume');
  return data;
}

/** Toggle auto-renewal. Off schedules an end-of-period cancellation; on clears it. */
export async function apiSetAutoRenew(enabled: boolean): Promise<CancellationState> {
  const { data } = await httpClient.post<CancellationState>('/api/billing/subscription/auto-renew', { enabled });
  return data;
}

/** Start an in-house card update; returns a SetupIntent client secret for Stripe Elements. */
export async function apiCreateSetupIntent(): Promise<{ clientSecret: string }> {
  const { data } = await httpClient.post<{ clientSecret: string }>('/api/billing/payment-method/setup-intent');
  return data;
}

/**
 * Start a hosted card add/update (redirect-based providers like Dodo); returns the
 * URL to send the user to. `url` is null for providers that use in-app Elements.
 */
export async function apiCreatePaymentMethodUpdateSession(): Promise<{ url: string | null }> {
  const { data } = await httpClient.post<{ url: string | null }>('/api/billing/payment-method/update-session');
  return data;
}

/** Finalise a card update: make the confirmed payment method the default. */
export async function apiSetDefaultPaymentMethod(
  paymentMethodId: string,
): Promise<{ paymentMethod: PaymentMethodSummary | null }> {
  const { data } = await httpClient.post<{ paymentMethod: PaymentMethodSummary | null }>(
    '/api/billing/payment-method/default',
    { paymentMethodId },
  );
  return data;
}

/**
 * Open the Stripe-hosted Customer Portal. Hidden fallback for advanced cases and
 * internal support only — day-to-day management happens inside Dunea.
 */
export async function apiCreateBillingPortal(): Promise<{ url: string }> {
  const { data } = await httpClient.post<{ url: string }>('/api/billing/portal');
  return data;
}
